import React, { useState, useEffect } from 'react';
import { Card, Spinner, Row, Col, Table } from 'react-bootstrap';
import { CardContent } from '../components/Card';
import { useLanguage } from '../context/LanguageContext';
import { usePageMetadata } from '../context/PageMetadataContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { formatNumber } from '../utils/format';
import { fetchPiPrice } from '../utils/price';
import AdSenseUnit from '../components/AdsenseUnit';
import AdUnit from '../components/AdUnit';

interface MigrationData {
  day: string;
  total_amount: number;
  formatted_date?: string; // For display purposes
  usd_value?: number;     // Added USD value
}

interface MigrationResponse {
  data: MigrationData[];
  generated_at: string;
}

const MigrateStatistics: React.FC = () => {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { setHeading, setTitle, setDescription } = usePageMetadata();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [migrationData, setMigrationData] = useState<MigrationData[]>([]);
  const [generatedAt, setGeneratedAt] = useState<string>("");
  const [totalMigrated, setTotalMigrated] = useState<number>(0);
  const [totalUsdValue, setTotalUsdValue] = useState<number>(0);
  const [piPrice, setPiPrice] = useState<number>(2); // Default price as fallback

  // Set page metadata
  useEffect(() => {
    setHeading(t('migrate_stats.heading'));
    setTitle(t('migrate_stats.title'));
    setDescription(t('migrate_stats.description'));
  }, [setHeading, setTitle, setDescription, t]);

  // Fetch Pi price
  useEffect(() => {
    const getPiPrice = async () => {
      const price = await fetchPiPrice();
      setPiPrice(price ?? 2); // Use default value if price is undefined
    };

    getPiPrice();
  }, []);

  // Fetch migration data
  useEffect(() => {
    const fetchMigrationData = async () => {
      try {
        setLoading(true);
        const response = await axios.get<MigrationResponse>('https://api.piscan.io/data/migratemainnet');
        
        if (response.data && response.data.data) {
          // Format dates for display and sort by date
          const formattedData = response.data.data.map(item => ({
            ...item,
            formatted_date: formatDate(item.day),
            usd_value: item.total_amount * piPrice
          })).sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
          
          setMigrationData(formattedData);
          setGeneratedAt(response.data.generated_at);
          
          // Calculate total migrated
          const total = formattedData.reduce((sum, item) => sum + item.total_amount, 0);
          setTotalMigrated(total);
          setTotalUsdValue(total * piPrice);
        } else {
          setError(t('migrate_stats.no_data'));
        }
      } catch (err) {
        console.error('Error fetching migration data:', err);
        setError(t('migrate_stats.fetch_error'));
      } finally {
        setLoading(false);
      }
    };

    // Only fetch migration data after we have the Pi price
    fetchMigrationData();
  }, [t, piPrice]); // Include piPrice in dependencies so calculations update when price changes

  // Format date from ISO string to more readable format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format USD value
  const formatUsd = (value: number): string => {
    return new Intl.NumberFormat(language, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  return (
    <div className="migrate-statistics-page">
        
          <p className="text-muted">{t('migrate_stats.description')}</p>
          <div className="small text-muted mb-2">
            {t('migrate_stats.last_updated')}: {new Date(generatedAt).toLocaleString()}
          </div>
      

      {/* Migration Data Table */}
      <Card>
        <CardContent>
          <h3 className="mb-4 stats-number">{t('migrate_stats.daily_stats')}</h3>
          
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">{t('migrate_stats.loading')}</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover>
                <thead>
                  <tr>
                    <th>{t('migrate_stats.date')}</th>
                    <th className="text-end">{t('migrate_stats.amount')}</th>
                    <th className="text-end">{t('migrate_stats.usd_value')}</th>
                  </tr>
                </thead>
                <tbody>
                  {migrationData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.formatted_date}</td>
                      <td className="text-end fw-bold">
                        {formatNumber(item.total_amount, language)} π
                      </td>
                      <td className="text-end">
                        {formatUsd(item.total_amount * piPrice)}
                      </td>
                    </tr>
                  ))}
                  <tr className="table-active">
                    <td><strong>{t('migrate_stats.total')}</strong></td>
                    <td className="text-end fw-bold">{formatNumber(totalMigrated, language)} π</td>
                    <td className="text-end fw-bold">{formatUsd(totalUsdValue)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          )}
          
          {!loading && generatedAt && (
            <div className="text-muted mt-4 small">
              <i className="bi bi-info-circle me-1"></i>
              {t('migrate_stats.data_note')} {t('migrate_stats.current_price_note', { price: formatUsd(piPrice) })}
            </div>
          )}
        </CardContent>
      </Card>
      <div className="row g-3 mb-4  d-block d-md-none">
        {/* <AdSenseUnit/> */}
        {/* <AdUnit
                  className="67cc9f95aa72d3d47fe128c2"
                  width="300px"
                  height="100px"
                  centered={true}
                /> */}
      </div>
      
    </div>
  );
};

export default MigrateStatistics;